import React, { useCallback, useEffect, useState } from 'react';
import { Project } from '../../types';
import { dossierAPI } from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BRAND, faseKleur } from '../../styles/brand';

interface ProjectFlowNavProps {
  project: Project;
  activeModule: string;
  onNavigate: (module: string) => void;
}

type FaseStatus = 'gevalideerd' | 'bezig' | 'open';

interface FaseNav {
  key: string;      // dossier-sectie
  module: string;   // route-module
  nr: number;
  label: string;
  short: string;
  icon: React.ReactNode;
  subs?: { module: string; label: string; icon: React.ReactNode }[];
}

const ic = (path: React.ReactNode) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
);

const FASEN: FaseNav[] = [
  {
    key: 'projectverkenning', module: 'projectverkenning', nr: 1, label: 'Projectverkenning',
    short: 'Doel, context en uitgangspunten van het project.',
    icon: ic(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="0.6" fill="currentColor" /></>),
  },
  {
    key: 'omgevingsanalyse', module: 'omgevingsanalyse', nr: 2, label: 'Omgevingsanalyse',
    short: 'Analyse van de omgeving, trends en stakeholders.',
    icon: ic(<><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></>),
    subs: [
      { module: 'gebiedsanalyse', label: 'Gebiedskaart', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg> },
      { module: 'mediadashboard', label: 'Nieuws & sentiment', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v10a2 2 0 0 0 2 2z" /><line x1="6" y1="9" x2="13" y2="9" /><line x1="6" y1="13" x2="13" y2="13" /></svg> },
      { module: 'stakeholders', label: 'Stakeholders', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> },
    ],
  },
  {
    key: 'personas', module: 'personas-fase', nr: 3, label: "Persona's",
    short: 'Inzicht in doelgroepen en hun behoeften.',
    icon: ic(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  },
  {
    key: 'strategie', module: 'strategie-fase', nr: 4, label: 'Communicatiestrategie',
    short: 'Boodschap, kanalen en middelen bepalen.',
    icon: ic(<><path d="M3 11l18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></>),
  },
  {
    key: 'kernboodschap', module: 'kernboodschap', nr: 5, label: 'Kernboodschap',
    short: 'De kernboodschap scherpstellen en valideren.',
    icon: ic(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />),
  },
  {
    key: 'communicatiewerkplaats', module: 'werkplaats', nr: 6, label: 'Communicatiewerkplaats',
    short: 'Middelen opzetten vanuit de gevalideerde kernboodschap.',
    icon: ic(<><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></>),
  },
];

const lockIcon = (size: number) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
);

function hasContent(content: any): boolean {
  if (!content || typeof content !== 'object') return false;
  return Object.values(content).some((v) =>
    (typeof v === 'string' && v.trim().length > 0) || (Array.isArray(v) && v.length > 0)
  );
}

function statusOf(section: any): FaseStatus {
  if (section?.status === 'locked') return 'gevalideerd';
  return hasContent(section?.content) ? 'bezig' : 'open';
}

export const ProjectFlowNav: React.FC<ProjectFlowNavProps> = ({ project, activeModule, onNavigate }) => {
  const isMobile = useIsMobile(980);
  const [statuses, setStatuses] = useState<FaseStatus[]>(() => FASEN.map(() => 'open'));

  const load = useCallback(async () => {
    try {
      const res = await dossierAPI.getByProject(project.id);
      const sections = res?.dossier?.sections || {};
      setStatuses(FASEN.map((f) => statusOf(sections[f.key])));
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load, activeModule]);

  // Direct verversen zodra een fase wordt gevalideerd/heropend/opgeslagen (geen reload nodig).
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener('dossier:changed', onChange);
    return () => window.removeEventListener('dossier:changed', onChange);
  }, [load]);

  const isActive = (f: FaseNav) => activeModule === f.module || (f.subs || []).some((s) => s.module === activeModule);

  // ---- Mobiel: horizontale strip ----
  if (isMobile) {
    return (
      <nav style={{ background: '#fff', borderBottom: '1px solid #ececf1', padding: '12px 14px' }}>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {FASEN.map((f, i) => {
            const active = isActive(f);
            const stt = statuses[i];
            const fk = faseKleur(f.nr);
            const gevuld = stt !== 'open' || active;
            return (
              <button key={f.key} onClick={() => onNavigate(f.module)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: '7px 12px 7px 8px', borderRadius: 999, border: `1px solid ${active ? fk.border : '#ececf1'}`, background: active ? fk.soft : '#fff', cursor: 'pointer' }}>
                <span style={{ width: 26, height: 26, borderRadius: '50%', background: gevuld ? fk.base : '#eef0f4', color: gevuld ? BRAND.groen : '#9aa1b0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{stt === 'gevalideerd' ? lockIcon(13) : f.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: active ? BRAND.groen : '#1a1a2e', whiteSpace: 'nowrap' }}>{f.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  // ---- Desktop: verticale rail ----
  return (
    <aside style={{ width: 288, flexShrink: 0, height: '100%', overflowY: 'auto', background: '#fff', borderRight: '1px solid #ececf1', padding: '20px 20px 24px', boxSizing: 'border-box' }}>
      <button onClick={() => onNavigate('dashboard')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 22, padding: '6px 10px 6px 6px', borderRadius: 8, border: 'none', background: 'transparent', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        Overzicht
      </button>

      <div style={{ position: 'relative' }}>
        {FASEN.map((f, i) => {
          const active = isActive(f);
          const stt = statuses[i];
          const last = i === FASEN.length - 1;
          // Kleur per fase conform het compas; open fasen blijven neutraal.
          const fk = faseKleur(f.nr);
          const gevuld = stt !== 'open' || active;
          return (
            <div key={f.key} style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 40, flexShrink: 0 }}>
                <button onClick={() => onNavigate(f.module)} title={f.label}
                  style={{ position: 'relative', width: 40, height: 40, borderRadius: '50%', background: gevuld ? fk.base : '#eef0f4', color: gevuld ? BRAND.groen : '#9aa1b0', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', boxShadow: active ? `0 0 0 4px ${fk.border}` : 'none', flexShrink: 0 }}>
                  {f.icon}
                  {stt === 'gevalideerd' && (
                    <span style={{ position: 'absolute', right: -4, bottom: -4, width: 18, height: 18, borderRadius: '50%', background: BRAND.groen, color: '#fff', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{lockIcon(9)}</span>
                  )}
                </button>
                {!last && <span style={{ flex: 1, width: 2, background: stt === 'gevalideerd' ? fk.border : '#eceef2', marginTop: 4 }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0, marginBottom: last ? 0 : 18 }}>
                <button onClick={() => onNavigate(f.module)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: active ? `1px solid ${fk.border}` : '1px solid transparent', background: active ? fk.soft : 'transparent', borderRadius: 10, padding: active ? '9px 11px' : '7px 0', margin: active ? '0 -4px' : 0, cursor: 'pointer' }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: BRAND.groen }}>{f.label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: '#8a90a0', lineHeight: 1.4, marginTop: 3 }}>{f.short}</span>
                  {stt === 'gevalideerd' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: BRAND.groen, color: '#fff', marginTop: 7 }}>{lockIcon(10)} Gevalideerd</span>
                  )}
                  {stt === 'bezig' && (
                    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: fk.soft, border: `1px solid ${fk.border}`, color: BRAND.groen, marginTop: 7 }}>In bewerking</span>
                  )}
                </button>
                {(f.subs || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {(f.subs || []).map((s) => {
                      const sActive = activeModule === s.module;
                      return (
                        <button key={s.module} onClick={() => onNavigate(s.module)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 9px', borderRadius: 8, border: '1px solid', borderColor: sActive ? fk.border : '#e5e7eb', background: sActive ? fk.soft : '#fff', color: sActive ? BRAND.groen : '#4b5563', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                          {s.icon}
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer-link: geconsolideerd dossier (buiten de 5 fasen) */}
      <button onClick={() => onNavigate('dossier')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', marginTop: 20, padding: '11px 12px', borderRadius: 10, border: '1px solid', borderColor: activeModule === 'dossier' ? '#b9c8b4' : '#e5e7eb', background: activeModule === 'dossier' ? '#f4f6f1' : '#fff', color: activeModule === 'dossier' ? '#1e3a2f' : '#374151', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, background: activeModule === 'dossier' ? '#e8eee6' : '#f1f2f5', color: activeModule === 'dossier' ? '#1e3a2f' : '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></svg>
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>Volledig dossier</span>
          <span style={{ display: 'block', fontSize: 11.5, color: '#8a90a0', marginTop: 1 }}>Overzicht &amp; export</span>
        </span>
      </button>
    </aside>
  );
};
